import { IntlShape } from "react-intl";
import moment from "moment";
import momentTZ from "moment-timezone";
import { FieldValues, UseFormClearErrors, UseFormGetValues, UseFormSetError } from "react-hook-form";

import { StopAddressType, StopTimeType, StopType } from "types/MultiStop";
import { ScheduleStopValue } from "pages/shipper/RequestFormV2/defaults/scheduleDefaults";
import { formatStopValidationDateTime } from "@RequestFormV2/sections/Schedule/utils/scheduleDateTime";
import { setRequiredFieldError, setTimeFieldError } from "@RequestFormV2/validation/utils/setFieldErrors";
import {
  getValidTimeMessage,
  hasCompleteTimeInput,
  isEmptyValue,
  isValidTimeValue,
} from "@RequestFormV2/validation/utils/validationMessages";

function validateAvailableHoursField(
  fieldPath: string,
  value: string | null | undefined,
  intl: IntlShape,
  setError: UseFormSetError<FieldValues>,
  clearErrors?: UseFormClearErrors<FieldValues>,
): boolean {
  if (isEmptyValue(value)) {
    clearErrors?.(fieldPath);
    return true;
  }

  if (!hasCompleteTimeInput(value)) {
    clearErrors?.(fieldPath);
    return true;
  }

  if (!isValidTimeValue(value)) {
    setError(fieldPath, {
      type: "manual",
      message: getValidTimeMessage(intl),
    });
    return false;
  }

  return true;
}

export function validateHoursOrder(
  stops: ScheduleStopValue[],
  intl: IntlShape,
  setError: UseFormSetError<FieldValues>,
  clearErrors?: UseFormClearErrors<FieldValues>,
): boolean {
  let isValid = true;

  stops.forEach((stop, index) => {
    const startField = `schedule.stops.${index}.stop_hours_start`;
    const endField = `schedule.stops.${index}.stop_hours_end`;
    const hoursStart = stop.stop_hours_start;
    const hoursEnd = stop.stop_hours_end;

    const startFieldValid = validateAvailableHoursField(startField, hoursStart, intl, setError, clearErrors);
    const endFieldValid = validateAvailableHoursField(endField, hoursEnd, intl, setError, clearErrors);

    if (!startFieldValid || !endFieldValid) {
      isValid = false;
      return;
    }

    if (isValidTimeValue(hoursStart) && isValidTimeValue(hoursEnd)) {
      const start = moment(hoursStart, "HH:mm:ss");
      const end = moment(hoursEnd, "HH:mm:ss");
      if (!end.isAfter(start)) {
        setError(endField, {
          type: "hoursOrder",
          message: intl.formatMessage({
            id: "request-form.schedule.validation.hoursEndAfterStart",
            defaultMessage: "End time should be greater than start time",
          }),
        });
        isValid = false;
      } else {
        clearErrors?.(endField);
      }
    }
  });

  return isValid;
}

export function isStopHoursOrderValid(stop: ScheduleStopValue): boolean {
  const hoursStart = stop.stop_hours_start;
  const hoursEnd = stop.stop_hours_end;

  if (!isValidTimeValue(hoursStart) || !isValidTimeValue(hoursEnd)) return true;

  const start = moment(hoursStart, "HH:mm:ss");
  const end = moment(hoursEnd, "HH:mm:ss");
  return end.isAfter(start);
}

export function getStopsWithLatestHourValue(
  stops: ScheduleStopValue[],
  fieldPath: string,
  time?: string,
): ScheduleStopValue[] {
  if (!time) return stops;

  const match = fieldPath.match(/^schedule\.stops\.(\d+)\.(stop_hours_start|stop_hours_end)$/);
  if (!match) return stops;

  const index = Number(match[1]);
  const field = match[2] as "stop_hours_start" | "stop_hours_end";
  const nextStops = [...stops];
  nextStops[index] = { ...nextStops[index], [field]: time };
  return nextStops;
}

export const DELIVERY_SCHEDULE_ORDER_ERROR_PATH = "schedule.stops.1.delivery_schedule_order";
export const DELIVERY_ADDRESS_MATCH_ERROR_PATH = "schedule.stops.1.address_match";
export const AT_LEAST_ONE_DELIVERY_ERROR_PATH = "schedule.multistop.types";

export function validateAtLeastOneDelivery(
  stops: ScheduleStopValue[],
  intl: IntlShape,
  setError: UseFormSetError<FieldValues>,
): boolean {
  // Only relevant for multi-stop requests (>2 stops); 2-stop requests always have a delivery.
  if (stops.length <= 2) return true;
  if (stops.some((stop) => stop.stop_type === StopType.DROPOFF)) return true;

  setError(AT_LEAST_ONE_DELIVERY_ERROR_PATH, {
    type: "manual",
    message: intl.formatMessage({
      id: "request-form.schedule.validation.atLeastOneDelivery",
      defaultMessage: "At least one delivery is needed to complete the request",
    }),
  });
  return false;
}

export function validateDeliveryAfterPickup(
  stops: ScheduleStopValue[],
  intl: IntlShape,
  setError: UseFormSetError<FieldValues>,
  clearErrors?: UseFormClearErrors<FieldValues>,
): boolean {
  if (stops.length !== 2) return true;

  const pickup = stops[0];
  const delivery = stops[1];
  if (!pickup || !delivery || delivery.stop_type !== StopType.DROPOFF) return true;

  const errorField = DELIVERY_SCHEDULE_ORDER_ERROR_PATH;

  // ASAP delivery has no scheduled datetime to compare against pickup.
  if (delivery.stop_time_type === StopTimeType.ASAP_DELIVERY) {
    clearErrors?.(errorField);
    return true;
  }

  const pickupDate = pickup.stop_after_date;
  const pickupTime = pickup.stop_after_time ?? "00:00:00";
  const pickupTz = pickup.stop_timezone ?? "";

  const deliveryDate =
    delivery.stop_time_type === StopTimeType.BEFORE ? delivery.stop_before_date : delivery.stop_after_date;
  const deliveryTime =
    (delivery.stop_time_type === StopTimeType.BEFORE ? delivery.stop_before_time : delivery.stop_after_time) ??
    "00:00:00";
  const deliveryTz = delivery.stop_timezone ?? "";

  if (!pickupDate || !deliveryDate) {
    clearErrors?.(errorField);
    return true;
  }

  const pickupDt = momentTZ.tz(`${pickupDate} ${pickupTime}`, "YYYY-MM-DD HH:mm:ss", pickupTz);
  const deliveryDt = momentTZ.tz(`${deliveryDate} ${deliveryTime}`, "YYYY-MM-DD HH:mm:ss", deliveryTz);

  if (!pickupDt.isValid() || !deliveryDt.isValid()) {
    clearErrors?.(errorField);
    return true;
  }

  if (!pickupDt.isBefore(deliveryDt)) {
    const pickupDateTime = formatStopValidationDateTime(pickupDate, pickup.stop_after_time);
    setError(errorField, {
      type: "manual",
      message: intl.formatMessage(
        {
          id: "request-form.schedule.validation.deliveryAfterPickup",
          defaultMessage:
            "Delivery schedule should be after {pickupDateTime} (note: timezone is considered as well)",
        },
        { pickupDateTime },
      ),
    });
    return false;
  }

  clearErrors?.(errorField);
  return true;
}

function addressesMatch(pickup: ScheduleStopValue, delivery: ScheduleStopValue): boolean {
  if (pickup.address_id && delivery.address_id) {
    return pickup.address_id === delivery.address_id;
  }

  const hasAnyField =
    pickup.street_address ||
    pickup.zipcode ||
    pickup.country_id ||
    delivery.street_address ||
    delivery.zipcode ||
    delivery.country_id;
  if (!hasAnyField) return false;

  return (
    pickup.street_address === delivery.street_address &&
    pickup.zipcode === delivery.zipcode &&
    String(pickup.country_id ?? "") === String(delivery.country_id ?? "")
  );
}

export function isDeliveryScheduleOrderValid(stops: ScheduleStopValue[]): boolean {
  if (stops.length !== 2) return true;

  const pickup = stops[0];
  const delivery = stops[1];
  if (!pickup || !delivery || delivery.stop_type !== StopType.DROPOFF) return true;

  if (delivery.stop_time_type === StopTimeType.ASAP_DELIVERY) return true;

  const pickupDate = pickup.stop_after_date;
  const pickupTime = pickup.stop_after_time ?? "00:00:00";
  const pickupTz = pickup.stop_timezone ?? "";

  const deliveryDate =
    delivery.stop_time_type === StopTimeType.BEFORE ? delivery.stop_before_date : delivery.stop_after_date;
  const deliveryTime =
    (delivery.stop_time_type === StopTimeType.BEFORE ? delivery.stop_before_time : delivery.stop_after_time) ??
    "00:00:00";
  const deliveryTz = delivery.stop_timezone ?? "";

  if (!pickupDate || !deliveryDate) return true;

  const pickupDt = momentTZ.tz(`${pickupDate} ${pickupTime}`, "YYYY-MM-DD HH:mm:ss", pickupTz);
  const deliveryDt = momentTZ.tz(`${deliveryDate} ${deliveryTime}`, "YYYY-MM-DD HH:mm:ss", deliveryTz);

  if (!pickupDt.isValid() || !deliveryDt.isValid()) return true;

  return pickupDt.isBefore(deliveryDt);
}

export function isDeliveryAddressMatchValid(stops: ScheduleStopValue[]): boolean {
  if (stops.length !== 2) return true;

  const pickup = stops[0];
  const delivery = stops[1];
  if (!pickup || !delivery) return true;

  return !addressesMatch(pickup, delivery);
}

export function validateAddressMatch(
  stops: ScheduleStopValue[],
  intl: IntlShape,
  setError: UseFormSetError<FieldValues>,
  clearErrors?: UseFormClearErrors<FieldValues>,
): boolean {
  // Address-match only applies to a single pickup/delivery pair. Once the request
  // becomes multi-stop (or drops below 2 stops) any previously-set error is stale.
  if (stops.length !== 2) {
    clearErrors?.(DELIVERY_ADDRESS_MATCH_ERROR_PATH);
    return true;
  }

  const pickup = stops[0];
  const delivery = stops[1];
  if (!pickup || !delivery) return true;

  if (addressesMatch(pickup, delivery)) {
    setError(DELIVERY_ADDRESS_MATCH_ERROR_PATH, {
      type: "manual",
      message: intl.formatMessage({
        id: "request-form.schedule.validation.addressCannotMatch",
        defaultMessage: "Delivery address cannot be the same as pickup address",
      }),
    });
    return false;
  }

  const hasDeliveryAddress = delivery.address_id || delivery.street_address;
  if (hasDeliveryAddress) {
    clearErrors?.(DELIVERY_ADDRESS_MATCH_ERROR_PATH);
  }
  return true;
}

function validateStopAddress(
  stop: ScheduleStopValue,
  stopIndex: number,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  isOptionalPortAddressFormEnabled: boolean,
): boolean {
  let isValid = true;
  const prefix = `schedule.stops.${stopIndex}`;
  const isAirportOrSeaport =
    stop.stop_address_type === StopAddressType.AIRPORT || stop.stop_address_type === StopAddressType.SEAPORT;
  const isAddressOptional = isOptionalPortAddressFormEnabled && isAirportOrSeaport;

  if (stop.stop_address_type === StopAddressType.AIRPORT) {
    if (!setRequiredFieldError(setError, `${prefix}.stop_airport_code`, stop.stop_airport_code, intl)) {
      isValid = false;
    }
  } else if (stop.stop_address_type === StopAddressType.SEAPORT) {
    if (!setRequiredFieldError(setError, `${prefix}.stop_seaport_code`, stop.stop_seaport_code, intl)) {
      isValid = false;
    }
  }

  if (
    !isAddressOptional &&
    !setRequiredFieldError(
      setError,
      `${prefix}.address_id`,
      stop.address_id || stop.address_name || stop.street_address,
      intl,
    )
  ) {
    isValid = false;
  }

  return isValid;
}

function validateStopDatetimeFields(
  stop: ScheduleStopValue,
  stopIndex: number,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
): boolean {
  if (stop.stop_time_type === StopTimeType.ASAP_DELIVERY) return true;

  let isValid = true;
  const prefix = `schedule.stops.${stopIndex}`;
  const isAfter = stop.stop_time_type === StopTimeType.AFTER;
  const isBefore = stop.stop_time_type === StopTimeType.BEFORE;
  const isBetween = stop.stop_time_type === StopTimeType.BETWEEN;

  if (isAfter || isBetween) {
    if (!setRequiredFieldError(setError, `${prefix}.stop_after_date`, stop.stop_after_date, intl)) isValid = false;
    if (!setTimeFieldError(setError, `${prefix}.stop_after_time`, stop.stop_after_time, intl)) isValid = false;
  }

  if (isBefore || isBetween) {
    if (!setRequiredFieldError(setError, `${prefix}.stop_before_date`, stop.stop_before_date, intl)) isValid = false;
    if (!setTimeFieldError(setError, `${prefix}.stop_before_time`, stop.stop_before_time, intl)) isValid = false;
  }

  if (!setRequiredFieldError(setError, `${prefix}.stop_timezone`, stop.stop_timezone, intl)) isValid = false;

  return isValid;
}

const NOTIFY_PARTY_VALIDATION_FIELDS = [
  "address_reference_number",
  "address_street",
  "address_street_line2",
  "address_city",
  "address_state",
  "address_country",
  "address_zipcode",
  "contact_name",
  "contact_phone",
  "contact_email",
  "airport",
  "seaport",
] as const;

function validateConsigneeOrNotifyAddress(
  prefix: string,
  values: Record<string, unknown>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
): boolean {
  let isValid = true;
  const addressType = values.address_type;

  if (addressType === "airport") {
    if (!setRequiredFieldError(setError, `${prefix}.airport`, values.airport, intl)) isValid = false;
  } else if (addressType === "seaport") {
    if (!setRequiredFieldError(setError, `${prefix}.seaport`, values.seaport, intl)) isValid = false;
  } else if (
    !setRequiredFieldError(
      setError,
      `${prefix}.address_id`,
      values.address_id || values.address_name || values.address_street,
      intl,
    )
  ) {
    isValid = false;
  }

  return isValid;
}

function validateConsignee(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
): boolean {
  const consignee = getValues("schedule.consignee") as Record<string, unknown> | undefined;
  if (!consignee) return true;

  if (consignee.same_as_delivery_address_contact) return true;

  let isValid = true;
  const prefix = "schedule.consignee";

  if (!validateConsigneeOrNotifyAddress(prefix, consignee, setError, intl)) isValid = false;

  (["contact_name", "contact_phone", "contact_email"] as const).forEach((field) => {
    if (!setRequiredFieldError(setError, `${prefix}.${field}`, consignee[field], intl)) {
      isValid = false;
    }
  });

  return isValid;
}

function validateNotifyParty(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
): boolean {
  const notifyParty = getValues("schedule.notify_party") as Record<string, unknown> | undefined;
  if (!notifyParty) return true;

  const sameAs = notifyParty.same_as_address_contact;
  if (sameAs === "sameAsConsigneeAddressContact" || sameAs === "sameAsDeliveryAddressContact") {
    return true;
  }

  const anyFieldFilled = NOTIFY_PARTY_VALIDATION_FIELDS.some((field) => !isEmptyValue(notifyParty[field]));
  if (!anyFieldFilled) return true;

  let isValid = true;
  const prefix = "schedule.notify_party";

  if (!validateConsigneeOrNotifyAddress(prefix, notifyParty, setError, intl)) isValid = false;

  (["contact_name", "contact_phone", "contact_email"] as const).forEach((field) => {
    if (!setRequiredFieldError(setError, `${prefix}.${field}`, notifyParty[field], intl)) {
      isValid = false;
    }
  });

  return isValid;
}

function validateClearance(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  options: {
    hasMultipleCountries: boolean;
    askClearance?: boolean;
    mode?: "quote" | "quick";
  },
): boolean {
  const showClearance =
    options.hasMultipleCountries && (options.mode !== "quick" || !!options.askClearance);
  if (!showClearance) return true;

  let isValid = true;
  const clearanceTypes = ["export", "import", "transit"] as const;

  clearanceTypes.forEach((type) => {
    const fieldName = `service.${type}_clearance`;
    const value = getValues(fieldName);

    if (!setRequiredFieldError(setError, fieldName, value, intl)) {
      isValid = false;
    }
  });

  return isValid;
}

export function validateSchedule(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  clearErrors: UseFormClearErrors<FieldValues>,
  intl: IntlShape,
  options: {
    showConsigneeSection: boolean;
    hasMultipleCountries: boolean;
    isOptionalPortAddressFormEnabled?: boolean;
    askClearance?: boolean;
    mode?: "quote" | "quick";
  },
): boolean {
  const stops = (getValues("schedule.stops") as ScheduleStopValue[]) ?? [];
  const isOptionalPortAddressFormEnabled = options.isOptionalPortAddressFormEnabled ?? false;

  let isValid = true;

  if (stops.length >= 2) {
    stops.forEach((stop, index) => {
      if (!validateStopAddress(stop, index, setError, intl, isOptionalPortAddressFormEnabled)) isValid = false;
      if (!validateStopDatetimeFields(stop, index, setError, intl)) isValid = false;
    });
  }

  if (!validateHoursOrder(stops, intl, setError, clearErrors)) isValid = false;
  if (!validateDeliveryAfterPickup(stops, intl, setError, clearErrors)) isValid = false;
  if (!validateAddressMatch(stops, intl, setError, clearErrors)) isValid = false;
  if (!validateAtLeastOneDelivery(stops, intl, setError)) isValid = false;

  if (options.showConsigneeSection) {
    if (!validateConsignee(getValues, setError, intl)) isValid = false;
    if (!validateNotifyParty(getValues, setError, intl)) isValid = false;
  }

  if (
    !validateClearance(getValues, setError, intl, {
      hasMultipleCountries: options.hasMultipleCountries,
      askClearance: options.askClearance,
      mode: options.mode,
    })
  ) {
    isValid = false;
  }

  return isValid;
}
